import type {FileUpload} from '@/components/file-upload'
import type PhoneInput from '@/components/phone-input'
import type Textarea from '@/components/textarea'
import type Checkbox from '@helios/shared/components/checkbox'
import type Input from '@helios/shared/components/input'
import type Select from '@helios/shared/components/select'

import type {validators} from './validators'

export enum FieldType {
    Text = 'text',
    Email = 'email',
    Phone = 'phone',
    Textarea = 'textarea',
    Select = 'select',
    Checkbox = 'checkbox',
    FileUpload = 'file',
}

export enum FieldWidth {
    Full = 'full',
    Half = 'half',
}

export type CustomError = {
    key: string
    value: string
}

export type StateValue = string | boolean | File

export interface State {
    [key: string]: StateValue
}

export enum ActionType {
    SET_VALUE,
}

type SetValue<TKey extends keyof State> = {
    type: ActionType.SET_VALUE
    key: keyof State
    value: State[TKey]
}

export type Action<TKey extends keyof State> = SetValue<TKey>
export type Validator = (state: State, key: keyof State) => boolean

export interface WithBasicInputProps<T extends FieldType, K extends keyof typeof validators> {
    key: keyof State
    type: T
    validators: Array<K>
    errorMessage: string
    width: FieldWidth
}

export type InputProps = Omit<React.ComponentProps<typeof Input>, 'onChange' | 'value'> &
    WithBasicInputProps<FieldType.Text, 'isRequired'>

type EmailProps = Omit<React.ComponentProps<typeof Input>, 'onChange' | 'value'> &
    WithBasicInputProps<FieldType.Email, 'isRequired' | 'isValidEmail'>

type TextareaProps = Omit<React.ComponentProps<typeof Textarea>, 'onChange' | 'value'> &
    WithBasicInputProps<FieldType.Textarea, 'isRequired'>

type PhoneProps = Omit<
    React.ComponentProps<typeof PhoneInput>,
    'setCountryCode' | 'setPhoneNumber' | 'countryCode' | 'phoneNumber'
> &
    WithBasicInputProps<FieldType.Phone, 'isRequired' | 'isValidPhoneNumber'>

type SelectProps = Omit<React.ComponentProps<typeof Select>, 'onChange' | 'value'> &
    WithBasicInputProps<FieldType.Select, 'isRequired'>

type CheckboxProps = Omit<React.ComponentProps<typeof Checkbox>, 'onChange' | 'value'> &
    WithBasicInputProps<FieldType.Checkbox, 'isRequired'>

type FileUploadProps = Omit<React.ComponentProps<typeof FileUpload>, 'onChange' | 'error'> &
    WithBasicInputProps<FieldType.FileUpload, 'isRequired'>

export type FieldProps =
    | InputProps
    | TextareaProps
    | EmailProps
    | PhoneProps
    | SelectProps
    | CheckboxProps
    | FileUploadProps
